import { useMemo, useState } from 'react';
import { Button, Select, Space, Tag, Typography, type TableProps } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ResponsiveList } from '@mall/shared';
import { listOrders } from './api';
import { ORDER_STATUS_COLOR, ORDER_STATUS_LABEL, type OrderStatus, type OrderView } from './types';

const PRICE = (cents: number) => `¥ ${(cents / 100).toFixed(2)}`;
const DT = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('zh-CN', { hour12: false }) : '—';

export function OrdersListPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');

  const query = useQuery({
    queryKey: ['admin', 'orders', page, pageSize],
    queryFn: () => listOrders({ page, pageSize }),
  });

  const dataSource = useMemo(() => {
    const list = query.data?.list ?? [];
    return statusFilter === 'ALL' ? list : list.filter((o) => o.status === statusFilter);
  }, [query.data, statusFilter]);

  const columns: TableProps<OrderView>['columns'] = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      width: 180,
      fixed: 'left',
      render: (no: string, record) => (
        <Link className="mall-row-title" to={`/orders/${record.id}`}>
          {no}
        </Link>
      ),
    },
    {
      title: '客户',
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.customerName}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {record.customerPhone || '—'}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '总额',
      dataIndex: 'totalCents',
      width: 120,
      align: 'right',
      render: (cents: number) => PRICE(cents),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (status: OrderStatus) => (
        <Tag color={ORDER_STATUS_COLOR[status]}>{ORDER_STATUS_LABEL[status]}</Tag>
      ),
    },
    {
      title: '行项目',
      dataIndex: 'items',
      width: 80,
      align: 'right',
      render: (items: OrderView['items']) => items.length,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 180,
      render: (iso: string) => DT(iso),
    },
  ];

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Orders</div>
          <Typography.Title level={3} className="mall-page-title">
            订单管理
          </Typography.Title>
          <p className="mall-page-description">查看订单、客户信息、支付状态和订单行数量。</p>
        </div>
        <Space>
          <Select<OrderStatus | 'ALL'>
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: 'ALL', label: '全部状态' },
              ...(
                ['PENDING_PAYMENT', 'PAID', 'SHIPPED', 'COMPLETED', 'CANCELLED'] as OrderStatus[]
              ).map((s) => ({ value: s, label: ORDER_STATUS_LABEL[s] })),
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={query.isFetching}
            onClick={() => query.refetch()}
          >
            刷新
          </Button>
        </Space>
      </div>
      <div className="mall-table-card">
        <div className="mall-table-toolbar">
          <div>
            <div className="mall-table-title">订单列表</div>
            <div className="mall-table-subtitle">
              当前页 {dataSource.length} 条，服务端共 {query.data?.total ?? 0} 条
            </div>
          </div>
          <div className="mall-table-actions">
            <Typography.Text type="secondary">
              {statusFilter === 'ALL' ? '全部状态' : ORDER_STATUS_LABEL[statusFilter]}
            </Typography.Text>
          </div>
        </div>
        <ResponsiveList<OrderView>
          rowKey={(record) => record.id}
          loading={query.isLoading}
          dataSource={dataSource}
          emptyText="暂无订单"
          pagination={{
            current: page,
            pageSize,
            total: query.data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`,
            pageSizeOptions: [10, 20, 50, 100, 200, 500, 1000, 2000, 5000],
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size);
            },
          }}
          tableProps={{
            size: 'small',
            columns,
            scroll: { x: 940 },
            locale: { emptyText: '暂无订单' },
          }}
          renderCard={(record) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Link className="mall-row-title" to={`/orders/${record.id}`}>
                  {record.orderNo}
                </Link>
                <Tag color={ORDER_STATUS_COLOR[record.status]}>
                  {ORDER_STATUS_LABEL[record.status]}
                </Tag>
              </div>
              <div className="mall-row-subtitle" style={{ marginTop: 4 }}>
                {record.customerName} · {record.customerPhone || '—'}
              </div>
              <div
                style={{
                  marginTop: 6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: 600 }}>{PRICE(record.totalCents)}</span>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {record.items.length} 项 · {DT(record.createdAt)}
                </span>
              </div>
            </div>
          )}
        />
      </div>
      {query.isError && (
        <Typography.Paragraph type="danger" style={{ marginTop: 16 }}>
          列表加载失败：{query.error instanceof Error ? query.error.message : '未知错误'}
        </Typography.Paragraph>
      )}
    </div>
  );
}
